<script setup>
import {
  provide,
  inject,
  ref,
  computed,
  watch,
  watchEffect,
  onBeforeUnmount,
  nextTick,
  onMounted,
  onServerPrefetch
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useWindowScroll, watchThrottled } from '@vueuse/core'
import { getArticleDetail, getArticleNext, getArticlePreview } from '@/api/article.js'
import MainArticle from './components/Article/index.vue'
import ArticleRecommend from './components/Recommend/index.vue'
import previewLogin from '../components/PreviewLogin/index.vue'
import { getSessionStorage, isBot } from '@/utils'

const route = useRoute()
const router = useRouter()
const { y: scrollY } = useWindowScroll()
const isPreview = inject('isPreview')
const perviewLogin = ref(false)
const perviewLoginKey = ref(false)
const isPreviewSecret = ref(false)

// 監聽加載更多
const scrollerFooter = ref()
let intersectionObserver
const initIntersectionObserver = () => {
  intersectionObserver = new IntersectionObserver(function (entries) {
    if (entries[0].intersectionRatio <= 0) return
    fetchArticleInfo()
  })
}
const observe = () => {
  if (import.meta.env.SSR) return
  if (!intersectionObserver) initIntersectionObserver()
  scrollerFooter.value && intersectionObserver.observe(scrollerFooter.value)
}
const unobserve = () =>
  scrollerFooter.value &&
  intersectionObserver &&
  intersectionObserver.unobserve(scrollerFooter.value) // 停止
const disconnect = () => {
  unobserve()
  intersectionObserver && intersectionObserver.disconnect() // 關閉
}

const articleId = ref(route?.params?.articleId)
const list = ref([])
const mainArticleRefs = ref([])
const isEmpty = computed(() => list.value.length === 0)
const initLoading = ref(true)
const loading = ref(false)
const finished = ref(false) // 最後頁完成狀態
const page = ref(1)
const limit = ref(3)
const getExcludeIds = (id) => {
  const index = list.value.findIndex((v) => v.id == id)
  if (index > 0) {
    const arr = list.value.slice(0, index)
    return arr.map((v) => v.id)
  }
  return []
}
const fetchArticleInfo = async (mode) => {
  const isInit = mode === 'init'
  if (!isInit) {
    loading.value = true
    unobserve()
  }
  try {
    const apiFun = isInit
      ? isPreview.value
        ? getArticlePreview
        : getArticleDetail
      : getArticleNext

    const extraParams = {}
    if (isPreview.value) {
      if (!import.meta.env.SSR) {
        const pwd = getSessionStorage(articleId.value)
        isPreviewSecret.value = !!pwd
        if (pwd) {
          extraParams.pwd = pwd
        }
      }
    }

    const apiParams = isInit
      ? { article_id: articleId.value, ...extraParams }
      : {
          article_id: articleId.value,
          query: { exclude_ids: getExcludeIds(articleId.value), ...extraParams }
        }
    const res = await apiFun(apiParams)

    if (isPreview.value && res.code === 103023) {
      if (!import.meta.env.SSR) return (window.location = '/403.html')
    }
    if (isPreview.value && res.code === 203043) {
      perviewLogin.value = true
      perviewLoginKey.value = !perviewLoginKey.value
    } else {
      perviewLogin.value = false
    }
    if (res.code !== 0) throw new Error(res.message)
    // console.log('文章详情--', res.data)
    let _data = res.data || {}

    if (isPreview.value && _data.source != 1) {
      router.replace(`/preview/vote/${articleId.value}`)
    }
    articleId.value = _data.id
    // 读取广告开关字段（默认显示广告）
    if (isInit) isShowAd.value = _data.is_show_ad ?? 1
    if (isInit) activeArticle.value = _data
    list.value.push(_data)

    if (page.value >= limit.value) finished.value = true
    else page.value++

    if (isInit) {
      initLoading.value = false
      if (!import.meta.env.SSR && !isBot()) nextTick(() => mainArticleRefs.value[0]?.play?.())
    } else loading.value = false
    if (isInit && !finished.value) nextTick(() => observe())
  } catch (error) {
    console.warn(error)
    if (isInit) initLoading.value = false
    else {
      loading.value = false
      finished.value = true
    }
  }
}

onMounted(() => {
  fetchArticleInfo('init')
})

onServerPrefetch(async () => {
  console.log('onServerPrefetch, fetchArticleInfo')
  await fetchArticleInfo('init')
  // if (!finished.value) await fetchArticleInfo()
})

const play = (_index) => {
  for (let index = 0; index < mainArticleRefs.value.length; index++) {
    const article = mainArticleRefs.value[index]
    if (index !== _index) {
      article.pause()
    }
  }
}

watch(loading, (val) => {
  if (val) unobserve()
  else if (finished.value) disconnect()
  else observe()
})

watchEffect(() => {
  // if (!initLoading.value && isEmpty.value && !perviewLogin.value)
  //   window.history.replaceState(history.state, null, '/notice.html')
})

// bottom为负数，取下一篇文章为当前文章，没有下一篇文章则还是当前文章。scrollY节流
const activeArticle = ref({})
watchThrottled(
  scrollY,
  () => {
    try {
      if (mainArticleRefs.value && mainArticleRefs.value.length > 0) {
        let arr1 = mainArticleRefs.value.map((v) => v.elementBounding.bottom.value - 64)
        let index = arr1.map((v) => (v < 0 ? 'false' : 'true')).lastIndexOf('false')
        if (index >= 0) {
          if (list.value[index + 1]) {
            activeArticle.value = list.value[index + 1]
          } else {
            activeArticle.value = list.value[index]
          }
        } else {
          activeArticle.value = list.value[0]
        }
      }
    } catch (error) {
      console.warn(error)
    }
  },
  { throttle: 500 }
)

provide('articleList', list)
provide('activeArticle', activeArticle)
// 广告开关：1=显示广告，2=隐藏所有广告（默认显示）
const isShowAd = ref(1)
provide('isShowAd', isShowAd)
onBeforeUnmount(() => {
  disconnect()
})

//禁用右鍵
// onMounted(() => {
// document.addEventListener('contextmenu', (e) =>{
//   e.preventDefault()
// })
// document.addEventListener('dragstart', (e) =>{
//   e.preventDefault()
// })
// })
</script>

<template>
  <div
    v-if="initLoading || isEmpty"
    class="init-page"
    :class="{ 'error-page': !initLoading && isEmpty }"
  >
    <div v-if="initLoading" class="tvb-loading-box init-loading-box"><n-spin /></div>
    <preview-login
      v-else-if="isPreview && perviewLogin"
      :key="perviewLoginKey"
      :value="articleId"
      :pwd-error="isPreviewSecret"
      :query-fun="() => fetchArticleInfo('init')"
    >
    </preview-login>
    <tvb-empty
      v-else
      icon="img_article_deleted"
      :des="isPreview ? '預覽鏈接已失效，請重新獲取' : '抱歉，找不到相關資料'"
      :showbtn="!isPreview"
    ></tvb-empty>
  </div>
  <template v-else>
    <div class="tvb-article-detail disable-content-copy">
      <div class="tvb-article-detail-wrap">
        <div class="tvb-article-detail_w">
          <template v-for="(item, index) in list" :key="item.id">
            <main-article
              :info="item"
              :index="index"
              ref="mainArticleRefs"
              @play="play(index)"
            ></main-article>
          </template>
        </div>

        <div v-if="loading" class="tvb-loading-box loading-box"><n-spin /></div>
        <div
          v-if="!finished && !isPreview && !isBot()"
          ref="scrollerFooter"
          class="scroller-footer"
          style="height: 0.5px"
        ></div>

        <article-recommend></article-recommend>
      </div>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.init-page {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  .init-loading-box {
    padding: 32px 0;
  }
}

.error-page {
  display: flex;
  align-items: center;
  justify-content: center;
  .tvb-empty {
    height: 500px;
  }
  &.s,
  &.xs {
    align-items: stretch;
    padding-top: 100px !important;
    padding-bottom: 100px !important;
    .tvb-empty {
      height: 400px;
    }
  }
}

.tvb-article-detail {
  padding-top: 16px !important;
  &.s,
  &.xs {
    padding-top: 10px !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    .tvb-article-detail_w {
      padding: 0 var(--tvb-article-detail-m-padding);
    }
  }

  .tvb-article-detail-wrap {
    max-width: var(--tvb-article-max-width);
    margin: 0 auto;
    .loading-box {
      padding: 32px 0;
      max-width: 848px;
    }
  }
}
</style>
